import { styled, css } from 'styled-components'

import { Button, ButtonAsA } from '@b-stock/fe-core/bstock-react'
import { Colors } from '@b-stock/fe-core/design-system'

import {
  CONTROL_HEIGHT,
  GAP_WIDTH,
  NARROW_FORM_WIDTH,
  PANEL_INPUT_WIDTH,
  WIDE_CTA_WIDTH,
  WIDE_FORM_WIDTH,
} from '../getActionPanelWidth'

/**
 * The form, whichever shape it takes: the amount input and a CTA, the CTA alone
 * across the same span, or — in `narrow` — the CTA collapsed to its icon.
 *
 * The width is explicit rather than `100%` because the bid states render inside
 * Formik's shrink-to-fit form element, where a percentage resolves against the
 * content instead of the cell. `Panel` sets the property per variant; the
 * fallback is the wide span, for the Compare panel, which renders outside it.
 */
export const FormRow = styled.div`
  align-items: center;
  display: flex;
  gap: ${GAP_WIDTH}px;
  width: var(--action-form-width, ${WIDE_FORM_WIDTH}px);
`

/**
 * Fills the same slot as `FormRow` while the form's data is still loading, so
 * the placeholder spinner sits centered in the space the input + CTA will
 * occupy instead of hugging the left edge — the panel's `justify-content:
 * space-between` positions a bare, intrinsically-sized spinner at the row's
 * flex-start, matching where the form used to start rather than the middle
 * of its span.
 */
export const CenteredFormRow = styled.div`
  align-items: center;
  display: flex;
  height: ${CONTROL_HEIGHT}px;
  justify-content: center;
  width: var(--action-form-width, ${WIDE_FORM_WIDTH}px);
`

/**
 * Holds the amount input, and anchors the bid error tooltip.
 */
export const FormInputWrapper = styled.div`
  flex-shrink: 0;
  width: ${PANEL_INPUT_WIDTH}px;

  > label {
    line-height: ${CONTROL_HEIGHT - 2}px;
    padding: 0 0.25rem;

    input {
      font-size: var(--ag-data-font-size);
      line-height: ${CONTROL_HEIGHT - 2}px;
      padding-left: 2px;
    }
  }
`

/**
 * The panel form's CTA. Takes whatever the row has left — all of it for the
 * states with no amount to enter, the rest of it for the two that have.
 *
 * The floor moves with the collapse. A text label needs room for "Raise Bid",
 * but once the button is down to its icon that floor is what would hold it at
 * label width, pushing it into the menu beside it — so collapsed it drops to
 * the same square the menu's buttons are.
 */
const formButton = css<{ $iconOnly: boolean }>`
  flex: 1;
  font-size: var(--ag-data-font-size);
  font-weight: normal;
  height: ${CONTROL_HEIGHT}px;
  justify-content: center;
  min-width: ${(o) => (o.$iconOnly ? NARROW_FORM_WIDTH : WIDE_CTA_WIDTH)}px;
  padding: 0 0.5rem;
  width: 100%;
`

export const FormButton = styled(Button)<{ $iconOnly: boolean }>`
  ${formButton}
`

/**
 * View Details navigates instead of submitting, so it is an anchor wearing the
 * same button. `ButtonAsA` is how the rest of the repo does this.
 */
export const FormLinkButton = styled(ButtonAsA)<{ $iconOnly: boolean }>`
  ${formButton}
`

/** The error message where the form has room to show it. */
export const ErrorText = styled.span`
  color: ${Colors.Semantic.Error.highlight};
`

/**
 * The collapsed error, in the same square as the controls beside it. Matches
 * fe-core's `InteractiveIcon` Error variant, which cannot be used here: it
 * renders its tooltip inside its own span, and AG Grid clips the cell.
 */
const errorIcon = css`
  align-items: center;
  color: ${Colors.Semantic.Error.highlight};
  display: flex;
  height: ${CONTROL_HEIGHT}px;
  justify-content: center;
  width: ${NARROW_FORM_WIDTH}px;

  &:hover {
    color: ${Colors.Semantic.Error.onhover};
  }
`

export const ErrorIcon = styled.span`
  ${errorIcon}
`

export const ErrorIconLink = styled.a`
  ${errorIcon}
`
